import React, { useMemo } from "react";
import {
  FileText,
  Eye,
  Download,
  Filter,
  ChevronLeft,
  ChevronRight,
  Upload,
  X,
  Edit2,
  Trash2,
  MoreHorizontal,
  Mail,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { usePagination } from "@/components/ui/table-pagination";
import {
  PatientDocument,
  documentTypes,
  DocumentData,
} from "@/lib/dashboard/patientDocumentsData";
import { useModalManager, ModalType } from "@/hooks/useModalManager";
import { usePatientDocumentsState } from "@/lib/dashboard/patientDocumentsData";
import Skeleton from "@mui/material/Skeleton";
import { useDocumentsData } from "@/components/Documents/documentsData";
import useTitanApp from "@/hooks/useTitanApp";

interface PatientDocumentsProps {
  documents: PatientDocument[];
  isDarkMode: boolean;
  onViewDocument: (document: PatientDocument) => void;
  onDownloadDocument: (document: PatientDocument) => void;
  onUploadDocument?: (documentData: {
    file: File;
    type: PatientDocument["type"];
    customName: string;
  }) => void;
  onRenameDocument?: (documentId: number, newName: string) => void;
  onDeleteDocument?: (documentId: number) => void;
  onEmailDocument?: (document: PatientDocument, emailBody: string) => void;
  onEmailMultipleDocuments?: (
    documents: PatientDocument[],
    emailBody: string,
  ) => void;
  patientEmail?: string;
  modalManager: ReturnType<typeof useModalManager>;
}

// Rename Input Component
function RenameInput({
  document,
  onRenameDocument,
  onClose,
  isDarkMode,
}: {
  document: PatientDocument | null;
  onRenameDocument?: (documentId: number, newName: string) => void;
  onClose: () => void;
  isDarkMode: boolean;
}) {
  const [newDocumentName, setNewDocumentName] = React.useState("");

  React.useEffect(() => {
    if (document) {
      setNewDocumentName(document.title);
    }
  }, [document]);

  const handleRename = () => {
    if (newDocumentName.trim() && document && onRenameDocument) {
      onRenameDocument(document.id, newDocumentName.trim());
      onClose();
    }
  };

  const handleClose = () => {
    setNewDocumentName("");
    onClose();
  };

  if (!document) return null;

  return (
    <>
      <Input
        value={newDocumentName}
        onChange={(e) => setNewDocumentName(e.target.value)}
        placeholder="Enter new document name"
        className={`${
          isDarkMode
            ? "bg-gray-700 border-gray-600 text-white"
            : "bg-white border-gray-300"
        }`}
        onKeyDown={(e) => {
          if (e.key === "Enter" && newDocumentName.trim()) {
            handleRename();
          } else if (e.key === "Escape") {
            handleClose();
          }
        }}
        autoFocus
      />
      <div className="flex justify-end gap-2 mt-4">
        <Button variant="outline" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          disabled={!newDocumentName.trim()}
          onClick={handleRename}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          Rename
        </Button>
      </div>
    </>
  );
}

// Bulk Email Documents Dialog Component
function BulkEmailDocumentsDialog({
  documents,
  patientEmail,
  onEmailMultipleDocuments,
  isDarkMode,
  open,
  onOpenChange,
}: {
  documents: PatientDocument[];
  patientEmail: string;
  onEmailMultipleDocuments?: (
    documents: PatientDocument[],
    emailBody: string,
    recipientEmail?: string,
    recipientType?: string,
  ) => void;
  isDarkMode: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { patientDocumentsLoader, currentPharmacy } =
    usePatientDocumentsState();
    const { patientState } = useTitanApp();
    const { selectedPatientDetails } = patientState

    const surgeryEmail = selectedPatientDetails?.surgery?.email?.trim();

  const [emailBody, setEmailBody] = React.useState("");
  const [recipientType, setRecipientType] = React.useState<
    "patient" | "surgery" | "other"
  >("patient");
  const [customEmail, setCustomEmail] = React.useState("");

  // Predefined recipient options
  const recipientOptions = {
    patient: {
      label: "Patient",
      email: patientEmail,
      greeting: "Dear Patient",
    },
    surgery: {
      label: "Surgery",
      email: surgeryEmail || "surgery@titanpharmacy.com", // Default surgery email
      greeting: "Dear Surgery Team",
    },
    other: {
      label: "Other",
      email: customEmail,
      greeting: "Dear Recipient",
    },
  };

  const currentRecipient = recipientOptions[recipientType];
  const recipientEmail =
    recipientType === "other" ? customEmail : currentRecipient.email;

  React.useEffect(() => {
    if (documents.length > 0 && open) {
      const documentList = documents.map((doc) => `• ${doc.title}`).join("\n");
      const greeting = currentRecipient.greeting;
      setEmailBody(`${greeting},

Please find attached the following documents for your records:

${documentList}

If you have any questions, please don't hesitate to contact us.

Best regards,
${currentPharmacy?.teamPharmacyName}`);
    } else if (!open) {
      // Reset all states when dialog is closed
      setEmailBody("");
      setRecipientType("patient");
      setCustomEmail("");
    }
  }, [documents, open, currentRecipient.greeting]);

  const handleSend = () => {
    if (
      documents.length > 0 &&
      emailBody.trim() &&
      recipientEmail &&
      onEmailMultipleDocuments
    ) {
      onEmailMultipleDocuments(
        documents,
        emailBody.trim(),
        recipientEmail,
        recipientType,
      );
      handleClose();
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setEmailBody("");
    setRecipientType("patient");
    setCustomEmail("");
  };

  if (documents.length === 0) return null;
  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        if (!open) {
          handleClose();
        }
      }}
    >
      <DialogContent
        className={`max-w-md ${
          isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"
        }`}
      >
        <DialogHeader>
          <DialogTitle
            className={`text-lg font-semibold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Email Documents
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div>
            <Label
              className={`text-sm font-medium mb-2 block ${
                isDarkMode ? "text-gray-200" : "text-gray-700"
              }`}
            >
              Send To
            </Label>
            <Select
              value={recipientType}
              onValueChange={(value: "patient" | "surgery" | "other") =>
                setRecipientType(value)
              }
            >
              <SelectTrigger
                className={`w-full ${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-white border-gray-300"
                }`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {patientEmail !== "" ? (
                  <SelectItem value="patient">
                    Patient ({patientEmail})
                  </SelectItem>
                ) : (
                  <></>
                )}

                {surgeryEmail && surgeryEmail !== "" ? (
                  <SelectItem value="surgery">
                   Surgery ({surgeryEmail})
                  </SelectItem>
                ) : (
                  <></>
                )}                
                <SelectItem value="other">Other (Custom Email)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {recipientType === "other" && (
            <div>
              <Label
                className={`text-sm font-medium mb-2 block ${
                  isDarkMode ? "text-gray-200" : "text-gray-700"
                }`}
              >
                Custom Email Address
              </Label>
              <Input
                value={customEmail}
                onChange={(e) => setCustomEmail(e.target.value)}
                placeholder="Enter email address"
                type="email"
                className={`${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-white border-gray-300"
                }`}
              />
            </div>
          )}

          {/* <div>
            <Label className={`text-sm font-medium mb-2 block ${isDarkMode ? "text-gray-200" : "text-gray-700"
              }`}>
              To (Email Address)
            </Label>
            <Input
              value={recipientEmail}
              disabled
              className={`${isDarkMode
                  ? "bg-gray-700 border-gray-600 text-white"
                  : "bg-gray-50 border-gray-300 text-gray-600"
                }`}
            />
          </div> */}

          <div>
            <Label
              className={`text-sm font-medium mb-2 block ${
                isDarkMode ? "text-gray-200" : "text-gray-700"
              }`}
            >
              Documents ({documents.length})
            </Label>
            <div
              className={`max-h-32 overflow-y-auto p-2 rounded border ${
                isDarkMode
                  ? "bg-gray-700 border-gray-600"
                  : "bg-gray-50 border-gray-300"
              }`}
            >
              {documents.map((document) => (
                <div
                  key={document.id}
                  className={`flex items-center gap-2 p-1 text-sm ${
                    isDarkMode ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  <FileText className="w-3 h-3 flex-shrink-0" />

                  {/* truncation container: flex-1 + min-w-0 so truncate works inside flex layouts */}
                  <div className="flex-1 min-w-0">
                    <span
                      className="truncate block max-w-[250px]"
                      title={document.title}
                    >
                      {document.title}
                    </span>
                  </div>
                  <Badge
                    variant="secondary"
                    className="text-xs ml-2 flex-shrink-0"
                  >
                    {document.type}
                  </Badge>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label
              className={`text-sm font-medium mb-2 block ${
                isDarkMode ? "text-gray-200" : "text-gray-700"
              }`}
            >
              Message
            </Label>
            <textarea
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              placeholder="Enter your message..."
              className={`w-full p-3 text-sm rounded border resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode
                  ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                  : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
              }`}
              rows={6}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              disabled={
                !emailBody.trim() ||
                !recipientEmail ||
                (recipientType === "other" && !customEmail.trim())
              }
              onClick={handleSend}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Mail className="w-4 h-4 mr-2" />
              Send Email ({documents.length} attachment
              {documents.length !== 1 ? "s" : ""})
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Email Document Dialog Component
function EmailDocumentDialog({
  document,
  patientEmail,
  onEmailDocument,
  isDarkMode,
  open,
  onOpenChange,
}: {
  document: PatientDocument | null;
  patientEmail: string;
  onEmailDocument?: (
    document: PatientDocument,
    emailBody: string,
    recipientEmail?: string,
    recipientType?: string,
  ) => void;
  isDarkMode: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { patientDocumentsLoader, currentPharmacy } =
    usePatientDocumentsState();
  const [emailBody, setEmailBody] = React.useState("");
  const [recipientType, setRecipientType] = React.useState<
    "patient" | "surgery" | "other"
  >("patient");
  const [customEmail, setCustomEmail] = React.useState("");

  // Predefined recipient options
  const recipientOptions = {
    patient: {
      label: "Patient",
      email: patientEmail,
      greeting: "Dear Patient",
    },
    // surgery: {
    //   label: "Surgery",
    //   email: "surgery@titanpharmacy.com", // Default surgery email
    //   greeting: "Dear Surgery Team",
    // },
    other: {
      label: "Other",
      email: customEmail,
      greeting: "Dear Recipient",
    },
  };

  const currentRecipient = recipientOptions[recipientType];
  const recipientEmail =
    recipientType === "other" ? customEmail : currentRecipient.email;

  React.useEffect(() => {
    if (document && open) {
      const greeting = currentRecipient.greeting;
      setEmailBody(`${greeting},

Please find attached the document "${document.title}" for your records.

If you have any questions, please don't hesitate to contact us.

Best regards,
${currentPharmacy?.teamPharmacyName}`);
    } else if (!open) {
      // Reset all states when dialog is closed
      setEmailBody("");
      setRecipientType("patient");
      setCustomEmail("");
    }
  }, [document, open, currentRecipient.greeting]);

  const handleSend = () => {
    if (document && emailBody.trim() && recipientEmail && onEmailDocument) {
      onEmailDocument(
        document,
        emailBody.trim(),
        recipientEmail,
        recipientType,
      );
      handleClose();
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setEmailBody("");
    setRecipientType("patient");
    setCustomEmail("");
  };

  if (!document) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        if (!open) {
          handleClose();
        }
      }}
    >
      <DialogContent
        className={`max-w-md ${
          isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"
        }`}
      >
        <DialogHeader>
          <DialogTitle
            className={`text-lg font-semibold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Email Document
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div>
            <Label
              className={`text-sm font-medium mb-2 block ${
                isDarkMode ? "text-gray-200" : "text-gray-700"
              }`}
            >
              Send To
            </Label>
            <Select
              value={recipientType}
              onValueChange={(value: "patient" | "surgery" | "other") =>
                setRecipientType(value)
              }
            >
              <SelectTrigger
                className={`w-full ${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-white border-gray-300"
                }`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {patientEmail !== "" ? (
                  <SelectItem value="patient">
                    Patient ({patientEmail})
                  </SelectItem>
                ) : (
                  <></>
                )}
                {/* <SelectItem value="surgery">
                  Surgery (surgery@titanpharmacy.com)
                </SelectItem> */}
                <SelectItem value="other">Other (Custom Email)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {recipientType === "other" && (
            <div>
              <Label
                className={`text-sm font-medium mb-2 block ${
                  isDarkMode ? "text-gray-200" : "text-gray-700"
                }`}
              >
                Custom Email Address
              </Label>
              <Input
                value={customEmail}
                onChange={(e) => setCustomEmail(e.target.value)}
                placeholder="Enter email address"
                type="email"
                className={`${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-white border-gray-300"
                }`}
              />
            </div>
          )}

          {/* <div>
            <Label className={`text-sm font-medium mb-2 block ${isDarkMode ? "text-gray-200" : "text-gray-700"
              }`}>
              To (Email Address)
            </Label>
            <Input
              value={recipientEmail}
              disabled
              className={`${isDarkMode
                  ? "bg-gray-700 border-gray-600 text-white"
                  : "bg-gray-50 border-gray-300 text-gray-600"
                }`}
            />
          </div> */}

          <div>
            <Label
              className={`text-sm font-medium mb-2 block ${
                isDarkMode ? "text-gray-200" : "text-gray-700"
              }`}
            >
              Document
            </Label>
            <div
              className={`flex items-center gap-2 p-2 rounded border ${
                isDarkMode
                  ? "bg-gray-700 border-gray-600"
                  : "bg-gray-50 border-gray-300"
              }`}
            >
              <FileText className="w-4 h-4" />
              <span className="text-sm">{document.title}</span>
            </div>
          </div>

          <div>
            <Label
              className={`text-sm font-medium mb-2 block ${
                isDarkMode ? "text-gray-200" : "text-gray-700"
              }`}
            >
              Message
            </Label>
            <textarea
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              placeholder="Enter your message..."
              className={`w-full p-3 text-sm rounded border resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode
                  ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                  : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
              }`}
              rows={6}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              disabled={
                !emailBody.trim() ||
                !recipientEmail ||
                (recipientType === "other" && !customEmail.trim())
              }
              onClick={handleSend}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Mail className="w-4 h-4 mr-2" />
              Send Email
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Document Viewer Dialog Component

export function DocumentViewerDialog({
  document,
  onDownloadDocument,
  isDarkMode,
  open,
  onOpenChange,
}: {
  document: DocumentData | null;
  onDownloadDocument?: (document: DocumentData) => void;
  isDarkMode: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const fileContent = useMemo(() => {
    if (!document?.base64Content || !document?.fileExtension) return null;

    let mimeType = "application/octet-stream";
    switch (document.fileExtension.toLowerCase()) {
      case ".pdf":
        mimeType = "application/pdf";
        break;
      case ".png":
      case ".jpg":
      case ".jpeg":
        mimeType = `image/${document.fileExtension.replace(".", "")}`;
        break;
      case ".txt":
        mimeType = "text/plain";
        break;
      case ".docx":
        mimeType =
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
        break;
    }

    return `data:${mimeType};base64,${document.base64Content}`;
  }, [document]);

  if (!document) return null;

  const handleClose = () => onOpenChange(false);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent
        className={`max-w-5xl max-h-[90vh] overflow-y-auto rounded-2xl ${
          isDarkMode
            ? "bg-gray-800 text-white border-gray-700"
            : "bg-white text-gray-900 border-gray-200"
        }`}
      >
        <DialogHeader>
          <DialogTitle
            className={`text-xl font-semibold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {document.title || "Document Viewer"}
          </DialogTitle>
        </DialogHeader>

        <div className="pt-4 space-y-6">
          {document.description && (
            <p
              className={`text-sm ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              {document.description}
            </p>
          )}

          {/* 📄 Document Preview */}
          {fileContent ? (
            <div
              className={`rounded-lg border overflow-hidden flex items-center justify-center bg-white ${
                isDarkMode ? "border-gray-700" : "border-gray-200"
              }`}
              style={{ minHeight: "400px", maxHeight: "70vh" }}
            >
              {document.fileExtension.toLowerCase() === ".pdf" ? (
                <iframe
                  src={fileContent}
                  className="w-full h-[70vh]"
                  title="PDF Viewer"
                />
              ) : document.fileExtension.toLowerCase() === ".docx" ? (
                <div className="flex flex-col items-center justify-center h-full text-center p-4">
                  <p className="text-sm mb-2">
                    DOCX preview is not supported. Please download to view.
                  </p>
                  <Button
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={() => onDownloadDocument?.(document)}
                  >
                    <Download className="w-4 h-4 mr-2" /> Download
                  </Button>
                </div>
              ) : (
                <img
                  src={fileContent}
                  alt={document.title || "Document Preview"}
                  className="max-w-full max-h-[70vh] object-contain cursor-pointer transition-transform hover:scale-105"
                  onClick={() => window.open(fileContent, "_blank")}
                />
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              Unable to preview this file type.
            </p>
          )}

          {/* 🔘 Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={handleClose}>
              Close
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => onDownloadDocument?.(document)}
            >
              <Download className="w-4 h-4 mr-2" /> Download
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function PatientDocuments({
  documents,
  isDarkMode,
  onViewDocument,
  onDownloadDocument,
  onUploadDocument,
  onRenameDocument,
  onDeleteDocument,
  onEmailDocument,
  onEmailMultipleDocuments,
  patientEmail,
  modalManager,
}: PatientDocumentsProps) {
  const [typeFilter, setTypeFilter] = React.useState<string>("all");
  const [uploadModalOpen, setUploadModalOpen] = React.useState(false);
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [selectedDocumentType, setSelectedDocumentType] =
    React.useState<PatientDocument["type"]>("GP Letter");
  const [customFileName, setCustomFileName] = React.useState("");
  const [fileError, setFileError] = React.useState<string>("");
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [dropdownOpen, setDropdownOpen] = React.useState<
    Record<string, boolean>
  >({});
  const { patientDocumentsLoader, currentPharmacy } =
    usePatientDocumentsState();
  // Multi-select state management
  const [selectedDocuments, setSelectedDocuments] = React.useState<number[]>(
    [],
  );
  const { documentSizeLimitInMbs } = useDocumentsData() || {};

  // Helper function to validate file size
  const validateFileSize = (file: File): boolean => {
    const maxSizeInBytes = documentSizeLimitInMbs * 1024 * 1024;
    return file.size <= maxSizeInBytes;
  };

  // Helper function to format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  // Validate file type
  const validateFileType = (file: File): boolean => {
    const validExtensions = [
      ".pdf",
      ".doc",
      ".docx",
      ".jpg",
      ".jpeg",
      ".png",
      ".txt",
    ];
    const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
    return validExtensions.includes(fileExtension);
  };

  // Handle file selection with validation
  const handleFileSelect = (file: File) => {
    // Clear previous errors
    setFileError("");

    // Validate file type
    if (!validateFileType(file)) {
      setFileError(
        "Invalid file type. Please select a PDF, DOC, DOCX, JPG, PNG, or TXT file.",
      );
      return;
    }

    // Validate file size
    if (!validateFileSize(file)) {
      setFileError(`File size exceeds ${documentSizeLimitInMbs}MB limit.`);
      return;
    }

    // If all validations pass
    setSelectedFile(file);
    setCustomFileName(file.name.replace(/\.[^/.]+$/, ""));
  };

  // Handle upload with final validation
  const handleUpload = () => {
    if (!selectedFile) {
      setFileError("Please select a file to upload.");
      return;
    }

    if (!customFileName.trim()) {
      setFileError("Please enter a document name.");
      return;
    }

    // Final validation
    if (!validateFileType(selectedFile)) {
      setFileError("Invalid file type. Please select a valid document.");
      return;
    }

    if (!validateFileSize(selectedFile)) {
      setFileError(`File size exceeds ${documentSizeLimitInMbs}MB limit.`);
      return;
    }

    // If all validations pass, proceed with upload
    if (onUploadDocument) {
      onUploadDocument({
        file: selectedFile,
        type: selectedDocumentType,
        customName: customFileName.trim(),
      });
      setUploadModalOpen(false);
      setSelectedFile(null);
      setCustomFileName("");
      setSelectedDocumentType("GP Letter");
      setFileError("");
    }
  };

  // Pagination setup - 5 items per page to match other panels
  const pagination = usePagination(5);
  const { currentPage, itemsPerPage, handlePageChange, getPaginatedData } =
    pagination;

  // Filter documents by type
// Filter documents by type AND sort by newest first
const filteredDocuments = React.useMemo(() => {
  let docs = typeFilter === "all" 
    ? documents 
    : documents.filter((doc) => doc.type === typeFilter);
  
  return docs.sort((a, b) => {
    return b.id.toString().localeCompare(a.id.toString());
  });
}, [documents, typeFilter]);

  // Reset to first page and clear selections when filter changes
  React.useEffect(() => {
    handlePageChange(1);
    setSelectedDocuments([]);
  }, [typeFilter, handlePageChange]);

  // Calculate pagination using the hook's utility function
  const { paginatedData: paginatedDocuments, totalPages } =
    getPaginatedData(filteredDocuments);
  // Helper functions to safely open dialogs after closing dropdown
  const safeOpenDialog = React.useCallback(
    (action: () => void, documentId: string) => {
      // Force close the dropdown first
      setDropdownOpen((prev) => ({ ...prev, [documentId]: false }));

      // Small delay to ensure dropdown is fully closed before opening dialog
      setTimeout(() => {
        action();
      }, 100);
    },
    [],
  );

  const handleDeleteClick = React.useCallback(
    (document: PatientDocument) => {
      safeOpenDialog(
        () => modalManager.openModal("documentDelete", document),
        document.id.toString(),
      );
    },
    [safeOpenDialog, modalManager],
  );

  const handleEmailClick = React.useCallback(
    (document: PatientDocument) => {
      safeOpenDialog(
        () => modalManager.openModal("documentEmail", document),
        document.id.toString(),
      );
    },
    [safeOpenDialog, modalManager],
  );

  const handleRenameClick = React.useCallback(
    (document: PatientDocument) => {
      safeOpenDialog(
        () => modalManager.openModal("documentRename", document),
        document.id.toString(),
      );
    },
    [safeOpenDialog, modalManager],
  );

  // Multi-select handlers
  const handleDocumentSelect = React.useCallback(
    (documentId: number, checked: boolean) => {
      if (checked) {
        setSelectedDocuments((prev) => [...prev, documentId]);
      } else {
        setSelectedDocuments((prev) => prev.filter((id) => id !== documentId));
      }
    },
    [],
  );

  const handleSelectAll = React.useCallback(
    (checked: boolean | "indeterminate") => {
      if (checked === true || checked === "indeterminate") {
        setSelectedDocuments(paginatedDocuments.map((doc) => doc.id));
      } else {
        setSelectedDocuments([]);
      }
    },
    [paginatedDocuments],
  );

  const handleBulkEmail = React.useCallback(() => {
    const selectedDocsData = documents.filter((doc) =>
      selectedDocuments.includes(doc.id),
    );
    modalManager.openModal("documentBulkEmail", selectedDocsData);
  }, [selectedDocuments, documents, modalManager]);

  // Computed selection states
  const isAllSelected =
    paginatedDocuments.length > 0 &&
    selectedDocuments.length === paginatedDocuments.length;
  const isPartiallySelected =
    selectedDocuments.length > 0 &&
    selectedDocuments.length < paginatedDocuments.length;

  // Reset selections when changing pages
  const handlePageChangeWithReset = React.useCallback(
    (page: number) => {
      handlePageChange(page);
      setSelectedDocuments([]);
    },
    [handlePageChange],
  );

  const getStatusColor = (status: PatientDocument["status"]) => {
    switch (status) {
      case "Available":
        return "text-green-600";
      case "Pending":
        return "text-orange-600";
      case "Expired":
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  const getDocumentIcon = (type: PatientDocument["type"]) => {
    switch (type) {
      case "GP Letter":
        return "bg-blue-100 text-blue-600";
      case "Lab Results":
        return "bg-green-100 text-green-600";
      case "Prescriptions":
        return "bg-purple-100 text-purple-600";
      case "Consent Letter":
        return "bg-orange-100 text-orange-600";
      case "Intervention":
        return "bg-cyan-100 text-cyan-600";
      case "General":
        return "bg-gray-100 text-gray-600";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  const getDocumentIconDark = (type: PatientDocument["type"]) => {
    switch (type) {
      case "GP Letter":
        return "bg-blue-900/30 text-blue-400";
      case "Lab Results":
        return "bg-green-900/30 text-green-400";
      case "Prescriptions":
        return "bg-purple-900/30 text-purple-400";
      case "Consent Letter":
        return "bg-orange-900/30 text-orange-400";
      case "Intervention":
        return "bg-cyan-900/30 text-cyan-400";
      case "General":
        return "bg-gray-900/30 text-gray-400";
      default:
        return "bg-gray-900/30 text-gray-400";
    }
  };

  const getVisiblePages = (
    currentPage: number,
    totalPages: number,
    maxVisible = 5,
  ) => {
    const half = Math.floor(maxVisible / 2);

    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, start + maxVisible - 1);

    // Adjust start if we're near the end
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }

    return { start, end };
  };

  return (
    <div
      className={`rounded-xl p-4 transition-colors duration-300 ${
        isDarkMode ? "bg-gray-800" : "bg-white"
      } shadow-sm w-full max-w-full overflow-hidden`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FileText
            className={`w-4 h-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
          />
          <h3
            className={`text-sm font-semibold transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Patient Documents
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-xs">
            {filteredDocuments.length}
          </Badge>
          {paginatedDocuments.length > 0 && (
            <div className="flex items-center gap-1">
              <Checkbox
                id="select-all-documents"
                checked={isPartiallySelected ? "indeterminate" : isAllSelected}
                onCheckedChange={handleSelectAll}
                className="h-4 w-4"
              />
              <label
                htmlFor="select-all-documents"
                className={`text-xs cursor-pointer transition-colors duration-300 ${
                  isDarkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                All
              </label>
            </div>
          )}
          <Dialog open={uploadModalOpen} onOpenChange={setUploadModalOpen}>
            <DialogTrigger asChild>
              <Button
                size="sm"
                className="h-7 px-2 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                disabled={patientDocumentsLoader}
              >
                <Upload className="w-3 h-3 mr-1" />
                Upload
              </Button>
            </DialogTrigger>
            <DialogContent
              className={`max-w-lg w-[90vw] ${
                isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"
              }`}
            >
              <DialogHeader>
                <DialogTitle
                  className={`text-lg font-semibold ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Upload Document
                </DialogTitle>
              </DialogHeader>

              {/* File Size Warning Banner */}
              {fileError && (
                <div
                  className={`flex items-center gap-2 rounded-lg px-4 py-2 border text-sm ${
                    isDarkMode
                      ? "bg-yellow-900/10 border-yellow-800 text-yellow-300"
                      : "bg-yellow-50 border-yellow-200 text-yellow-800"
                  }`}
                >
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>
                    Maximum file size is
                    <strong> {documentSizeLimitInMbs}MB.</strong> Larger files
                    will be rejected.
                  </span>
                </div>
              )}

              <div className="space-y-4 pt-4">
                {/* File Selection */}
                <div>
                  <Label
                    className={`text-sm font-medium mb-2 block ${
                      isDarkMode ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    Select File
                  </Label>

                  {/* Upload Area */}
                  {!selectedFile ? (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                        isDarkMode
                          ? "border-gray-600 hover:border-gray-500 bg-gray-700/50"
                          : "border-gray-300 hover:border-gray-400 bg-gray-50"
                      } `}
                    >
                      <Upload
                        className={`w-12 h-12 mx-auto mb-4 ${
                          isDarkMode ? "text-gray-400" : "text-gray-400"
                        }`}
                      />
                      <p
                        className={`text-sm mb-2 ${
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        Click to select a file
                      </p>
                      <p
                        className={`text-xs ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        PDF, DOC, DOCX, JPG, PNG, TXT (max{" "}
                        {documentSizeLimitInMbs}MB)
                      </p>
                    </div>
                  ) : (
                    <div
                      className={`border rounded-lg p-4 ${
                        isDarkMode
                          ? "bg-gray-700/50 border-gray-600"
                          : "bg-gray-50 border-gray-300"
                      }`}
                    >
                      <div
                        className={`border rounded-lg p-4 max-w-full overflow-hidden ${
                          isDarkMode
                            ? "bg-gray-700/50 border-gray-600"
                            : "bg-gray-50 border-gray-300"
                        }`}
                      >
                        <div className="flex items-start gap-3 w-full overflow-hidden">
                          <FileText className="w-8 h-8 flex-shrink-0 text-blue-600" />

                          <div className="flex-1 min-w-0 overflow-hidden">
                            <p
                              title={selectedFile.name}
                              className="text-sm font-medium
                   overflow-hidden
                   break-all
                   [display:-webkit-box]
                   [-webkit-line-clamp:2]
                   [-webkit-box-orient:vertical]"
                            >
                              {selectedFile.name}
                            </p>

                            <p className="text-xs text-gray-500 mt-1">
                              {formatFileSize(selectedFile.size)}
                            </p>
                          </div>

                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 flex-shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedFile(null);
                              setCustomFileName("");
                              setFileError("");
                            }}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleFileSelect(file);
                      }
                    }}
                  />
                </div>

                {/* Document Type Selection */}
                <div>
                  <Label
                    className={`text-sm font-medium mb-2 block ${
                      isDarkMode ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    Document Type
                  </Label>
                  <Select
                    value={selectedDocumentType}
                    onValueChange={setSelectedDocumentType}
                  >
                    <SelectTrigger
                      className={`w-full ${
                        isDarkMode
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300"
                      }`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {documentTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Custom File Name */}
                {selectedFile && (
                  <div className="w-full">
                    <Label
                      className={`text-sm font-medium mb-2 block ${
                        isDarkMode ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      Document Name
                    </Label>
                    <Input
                      value={customFileName}
                      onChange={(e) => setCustomFileName(e.target.value)}
                      placeholder="Enter document name"
                      className={`w-full ${
                        isDarkMode
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300"
                      }`}
                    />
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setUploadModalOpen(false);
                      setSelectedFile(null);
                      setCustomFileName("");
                      setSelectedDocumentType("GP Letter");
                      setFileError("");
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={
                      !selectedFile ||
                      !customFileName.trim() ||
                      !validateFileSize(selectedFile) ||
                      !validateFileType(selectedFile)
                    }
                    onClick={handleUpload}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Upload Document
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="flex items-center gap-2 mb-4">
        <Filter
          className={`w-3 h-3 ${
            isDarkMode ? "text-gray-400" : "text-gray-500"
          }`}
        />
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger
            className={`w-full text-xs h-8 ${
              isDarkMode
                ? "bg-gray-700 border-gray-600 text-white"
                : "bg-white border-gray-200"
            }`}
          >
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Documents</SelectItem>
            {documentTypes.map((type) => (
              <SelectItem key={type} value={type}>
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Bulk Actions Bar */}
      {selectedDocuments.length > 0 && (
        <div
          className={`mb-4 p-3 rounded-lg border transition-colors duration-300 ${
            isDarkMode
              ? "bg-blue-900/20 border-blue-800"
              : "bg-blue-50 border-blue-200"
          }`}
        >
          <div className="flex items-center justify-between">
            <span
              className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? "text-blue-300" : "text-blue-700"
              }`}
            >
              {selectedDocuments.length} document
              {selectedDocuments.length !== 1 ? "s" : ""} selected
            </span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={handleBulkEmail}
                disabled={!onEmailMultipleDocuments}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Mail className="w-4 h-4 mr-1" />
                Email Selected
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSelectedDocuments([])}
              >
                Clear Selection
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Documents List */}
      <div className="space-y-3 mb-4">
        {patientDocumentsLoader ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className={`border rounded-lg p-3 ${
                isDarkMode ? "border-gray-600" : "border-gray-200"
              }`}
            >
              <div className="flex items-start gap-3">
                <Skeleton
                  variant="rectangular"
                  width={24}
                  height={24}
                  sx={{ borderRadius: "4px" }}
                />
                <div className="flex-1 min-w-0">
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={20}
                    sx={{ bgcolor: isDarkMode ? "grey.800" : "grey.200" }}
                  />
                  <Skeleton
                    variant="text"
                    width="40%"
                    height={16}
                    sx={{ bgcolor: isDarkMode ? "grey.800" : "grey.200" }}
                  />
                </div>
                <Skeleton
                  variant="circular"
                  width={20}
                  height={20}
                  sx={{ bgcolor: isDarkMode ? "grey.800" : "grey.200" }}
                />
              </div>
            </div>
          ))
        ) : paginatedDocuments.length > 0 ? (
          paginatedDocuments.map((document) => (
            <div
              key={document.id}
              className={`border rounded-lg p-3 group transition-colors duration-300 ${
                isDarkMode ? "border-gray-600" : "border-gray-200"
              } ${
                selectedDocuments.includes(document.id)
                  ? isDarkMode
                    ? "bg-blue-900/20 border-blue-700"
                    : "bg-blue-50 border-blue-300"
                  : isDarkMode
                    ? "hover:bg-gray-700"
                    : "hover:bg-gray-50"
              }`}
            >
              <div className="flex items-start gap-3">
                <Checkbox
                  id={`document-${document.id}`}
                  checked={selectedDocuments.includes(document.id)}
                  onCheckedChange={(checked) =>
                    handleDocumentSelect(document.id, checked === true)
                  }
                  className="mt-1 h-4 w-4 flex-shrink-0"
                />
                <div
                  className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors duration-300 flex-shrink-0 ${
                    isDarkMode
                      ? getDocumentIconDark(document.type)
                      : getDocumentIcon(document.type)
                  }`}
                >
                  <FileText className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0 cursor-pointer">
                  <div className="flex items-start justify-between mb-1">
                    <div className="flex-1 min-w-0">
                      <h4
                        className={`text-xs font-medium truncate transition-colors duration-300 ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                        title={document.title}
                      >
                        {document.title}
                      </h4>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`text-xs transition-colors duration-300 ${
                            isDarkMode ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          {document.type}
                        </span>
                        <span
                          className={`text-xs ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          •
                        </span>
                        <span
                          className={`text-xs ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          {document.date}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                        <DropdownMenu
                          open={dropdownOpen[document.id.toString()] || false}
                          onOpenChange={(open) =>
                            setDropdownOpen((prev) => ({
                              ...prev,
                              [document.id.toString()]: open,
                            }))
                          }
                        >
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              title="More actions"
                            >
                              <MoreHorizontal className="w-3 h-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className={`${
                              isDarkMode
                                ? "bg-gray-800 border-gray-600"
                                : "bg-white border-gray-200"
                            }`}
                          >
                            <DropdownMenuItem
                              onClick={() => onViewDocument(document)}
                              className={`cursor-pointer ${
                                isDarkMode
                                  ? "text-gray-200 hover:bg-gray-700"
                                  : "text-gray-700 hover:bg-gray-50"
                              }`}
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              View Document
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => onDownloadDocument(document)}
                              className={`cursor-pointer ${
                                isDarkMode
                                  ? "text-gray-200 hover:bg-gray-700"
                                  : "text-gray-700 hover:bg-gray-50"
                              }`}
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Download
                            </DropdownMenuItem>
                            <DropdownMenuSeparator
                              className={`${
                                isDarkMode ? "bg-gray-600" : "bg-gray-200"
                              }`}
                            />
                            <DropdownMenuItem
                              onClick={() => handleRenameClick(document)}
                              className={`cursor-pointer ${
                                isDarkMode
                                  ? "text-gray-200 hover:bg-gray-700"
                                  : "text-gray-700 hover:bg-gray-50"
                              }`}
                            >
                              <Edit2 className="w-4 h-4 mr-2" />
                              Rename
                            </DropdownMenuItem>
                            {onEmailDocument && patientEmail && (
                              <DropdownMenuItem
                                onClick={() => handleEmailClick(document)}
                                className={`cursor-pointer ${
                                  isDarkMode
                                    ? "text-gray-200 hover:bg-gray-700"
                                    : "text-gray-700 hover:bg-gray-50"
                                }`}
                              >
                                <Mail className="w-4 h-4 mr-2" />
                                Email to Patient
                              </DropdownMenuItem>
                            )}
                            {onDeleteDocument && (
                              <DropdownMenuItem
                                onClick={() => handleDeleteClick(document)}
                                className={`cursor-pointer text-red-600 ${
                                  isDarkMode
                                    ? "hover:bg-red-900/20"
                                    : "hover:bg-red-50"
                                }`}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div
            className={`text-center py-8 ${
              isDarkMode ? "text-gray-400" : "text-gray-500"
            }`}
          >
            <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No documents found</p>
            {typeFilter !== "all" && (
              <p className="text-xs mt-1">Try adjusting your filter</p>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={modalManager.isModalOpen("documentDelete")}
        onOpenChange={(open) => !open && modalManager.closeModal()}
      >
        <AlertDialogContent
          className={`${
            isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"
          }`}
        >
          <AlertDialogHeader>
            <AlertDialogTitle
              className={`${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              Delete Document
            </AlertDialogTitle>
            <AlertDialogDescription
              className={`${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
            >
              Are you sure you want to delete "{modalManager.modalData?.title}"?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={modalManager.closeModal}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (modalManager.modalData && onDeleteDocument) {
                  onDeleteDocument(modalManager.modalData.id);
                }
                modalManager.closeModal();
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Email Document Dialog */}
      <EmailDocumentDialog
        document={modalManager.modalData}
        patientEmail={patientEmail || ""}
        onEmailDocument={onEmailDocument}
        isDarkMode={isDarkMode}
        open={modalManager.isModalOpen("documentEmail")}
        onOpenChange={(open) => {
          if (!open) {
            modalManager.closeModal();
          }
        }}
      />

      {/* Bulk Email Documents Dialog */}
      <BulkEmailDocumentsDialog
        documents={
          Array.isArray(modalManager.modalData) ? modalManager.modalData : []
        }
        patientEmail={patientEmail || ""}
        onEmailMultipleDocuments={onEmailMultipleDocuments}
        isDarkMode={isDarkMode}
        open={modalManager.isModalOpen("documentBulkEmail")}
        onOpenChange={(open) => {
          if (!open) {
            modalManager.closeModal();
          }
        }}
      />
      <DocumentViewerDialog
        document={modalManager.modalData || null}
        onDownloadDocument={onDownloadDocument}
        isDarkMode={isDarkMode}
        open={modalManager.isModalOpen("documentViewer")}
        onOpenChange={(open) => !open && modalManager.closeModal()}
      />
      {/* Rename Document Dialog */}
      <Dialog
        open={modalManager.isModalOpen("documentRename")}
        onOpenChange={(open) => !open && modalManager.closeModal()}
      >
        <DialogContent
          className={`max-w-lg w-[90vw] ${
            isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"
          }`}
        >
          <DialogHeader>
            <DialogTitle
              className={`text-lg font-semibold ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Rename Document
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label
                className={`text-sm font-medium mb-2 block ${
                  isDarkMode ? "text-gray-200" : "text-gray-700"
                }`}
              >
                Document Name
              </Label>
              <RenameInput
                document={modalManager.modalData}
                onRenameDocument={onRenameDocument}
                onClose={modalManager.closeModal}
                isDarkMode={isDarkMode}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pagination Controls */}
      {filteredDocuments.length > itemsPerPage && totalPages > 1 && (
        <div
          className={`mt-4 pt-3 border-t flex items-center justify-between transition-colors duration-300 ${
            isDarkMode ? "border-gray-600" : "border-gray-200"
          }`}
        >
          <div
            className={`text-xs transition-colors duration-300 ${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            Showing {(currentPage - 1) * itemsPerPage + 1}-
            {Math.min(currentPage * itemsPerPage, filteredDocuments.length)} of{" "}
            {filteredDocuments.length}
          </div>

          <div className="flex items-center gap-1">
            {/* Previous Button */}
            <button
              onClick={() => handlePageChangeWithReset(currentPage - 1)}
              disabled={currentPage === 1}
              className={`p-1 rounded transition-colors duration-300 ${
                currentPage === 1
                  ? isDarkMode
                    ? "text-gray-600 cursor-not-allowed"
                    : "text-gray-400 cursor-not-allowed"
                  : isDarkMode
                    ? "text-gray-300 hover:bg-gray-700"
                    : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {/* Page Numbers */}

            {(() => {
              const { start, end } = getVisiblePages(currentPage, totalPages);

              return (
                <>
                  {start > 1 && (
                    <>
                      <button
                        onClick={() => handlePageChangeWithReset(1)}
                        className="px-2 py-1 text-xs rounded text-gray-600 hover:bg-gray-100"
                      >
                        1
                      </button>
                      <span className="px-1 text-xs text-gray-400">…</span>
                    </>
                  )}

                  {Array.from(
                    { length: end - start + 1 },
                    (_, i) => start + i,
                  ).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChangeWithReset(page)}
                      className={`px-2 py-1 text-xs rounded transition-colors ${
                        page === currentPage
                          ? isDarkMode
                            ? "bg-blue-600 text-white"
                            : "bg-blue-500 text-white"
                          : isDarkMode
                            ? "text-gray-300 hover:bg-gray-700"
                            : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      {page}
                    </button>
                  ))}

                  {end < totalPages && (
                    <>
                      <span className="px-1 text-xs text-gray-400">…</span>
                      <button
                        onClick={() => handlePageChangeWithReset(totalPages)}
                        className="px-2 py-1 text-xs rounded text-gray-600 hover:bg-gray-100"
                      >
                        {totalPages}
                      </button>
                    </>
                  )}
                </>
              );
            })()}

            {/* Next Button */}
            <button
              onClick={() => handlePageChangeWithReset(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`p-1 rounded transition-colors duration-300 ${
                currentPage === totalPages
                  ? isDarkMode
                    ? "text-gray-600 cursor-not-allowed"
                    : "text-gray-400 cursor-not-allowed"
                  : isDarkMode
                    ? "text-gray-300 hover:bg-gray-700"
                    : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
